/**
 * ApplicationNextStep — что делать с заявкой: позвонить и завести клиента.
 *
 * Раньше на этом месте стояла подсказка «идите в "Клиенты" и создайте
 * аккаунт вручную». Из-за неё данные заявки переписывались глазами, а
 * связи между заявкой и заведённым клиентом не оставалось вовсе —
 * очередь не знала, разобрана заявка или нет. Теперь кнопка открывает
 * форму предзаполненной и уносит с собой `applicationId`.
 *
 * Про места говорим прямо и называем цифру. В заявке лежит диапазон, а
 * в счёте нужна цифра — вывести одно из другого нельзя, поэтому поле
 * остаётся на умолчании. Промолчать тут нельзя: менеджер решит, что
 * места пришли из заявки, и выпустит счёт не на то число врачей.
 *
 * `convertedClinicId` уже есть — значит клиента завели. Кнопка тогда
 * ведёт не в карточку клиента (маршрута по id у неё нет), а в список с
 * поиском по номеру владельца. Называется она соответственно: обещать
 * «открыть карточку» и высадить человека в список — хуже, чем честно
 * назвать действие поиском.
 */

import {ExternalLink, MessageCircle, UserPlus} from 'lucide-react';

import type {ApplicationItem} from '@services/applications';
import {Button} from '@components/ui/Button';
import {DEFAULT_SEATS} from '@pages/Clients/CreateAccountModal';

type Props = {
  application: ApplicationItem;
  onConvert: (application: ApplicationItem) => void;
  onOpenClient: (phone: string) => void;
};

export const ApplicationNextStep = ({application: a, onConvert, onOpenClient}: Props) => {
  const handleFindClient = () => onOpenClient(a.phone);
  const handleConvert = () => onConvert(a);
  const converted = a.convertedClinicId !== null;

  return (
    <>
      <a
        href={`https://wa.me/${a.phone.replace(/\D/g, '')}`}
        target="_blank"
        rel="noreferrer"
        className="mt-3.5 flex h-11 items-center justify-center gap-2 rounded-[11px] border border-input bg-card text-sm font-semibold text-skr-paid"
      >
        <MessageCircle className="size-4" strokeWidth={2} />
        Написать в WhatsApp
      </a>

      <Button
        variant="outline"
        className="mt-3.5 h-11 w-full rounded-[11px]"
        onClick={converted ? handleFindClient : handleConvert}
      >
        {converted ? (
          <ExternalLink className="size-4" strokeWidth={2} />
        ) : (
          <UserPlus className="size-4" strokeWidth={2} />
        )}
        {converted ? 'Найти клиента по номеру' : 'Завести клиента'}
      </Button>

      <div className="mt-2 text-[11.5px] leading-[1.45] text-muted-foreground">
        {converted
          ? 'По этой заявке клиент уже заведён. Карточка открывается из списка «Клиенты» — ищем по номеру владельца заявки.'
          : `Форма откроется с данными заявки. Мест в ней будет ${DEFAULT_SEATS} — это умолчание, не из заявки: в ней только диапазон. Уточните на звонке.`}
      </div>
    </>
  );
};
